import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { TextField, Paper, MenuItem, Box } from "@mui/material";

const getLabel = (item) =>
  typeof item === "object" && item !== null
    ? String(item.label ?? "")
    : String(item ?? "");

const getValue = (item) =>
  typeof item === "object" && item !== null
    ? String(item.label ?? "")
    : String(item ?? "");
const extractCurrentWord = (text, cursorPos) => {
  const safeText = text ?? "";
  const safeCursor =
    typeof cursorPos === "number" ? cursorPos : safeText.length;
  const before = safeText.slice(0, safeCursor);
  const match = before.match(/(\S+)$/);
  return match
    ? { word: match[1], start: safeCursor - match[1].length }
    : { word: "", start: safeCursor };
};

export default function InputAreaDropdown({
  name,
  label,
  control,
  defaultValue = "",
  suggestions = [],
  handleInputChange,
  minRows = 2,
  maxRows = 6,
  isDisabled,
  isMandatory,
  error,
  onSelectedIdChange,
  onSuggestionSelect
}) {
  const [filtered, setFiltered] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);

  const wrapperRef = useRef(null);
  const valueRef = useRef("");
  const wordRef = useRef("");
  const wordStartRef = useRef(0);
  const onChangeRef = useRef(null);
  const selectedStringsRef = useRef([]);

  const filterSuggestions = useCallback(
    (word) => {
      if (!word || !Array.isArray(suggestions)) return [];
      const lower = word.toLowerCase();
      return suggestions.filter((item) =>
        getLabel(item).toLowerCase().includes(lower),
      );
    },
    [suggestions],
  );

  const getSelectedId = (text) => {
    const matched = suggestions.filter((s) => text.includes(getValue(s)));

    return matched.length === 1 ? matched[0]?.id : null;
  };

  const handleChange = (e, rhfOnChange) => {
    if (!e?.target) return;

    const val = e.target.value ?? "";
    const cursor = e.target.selectionStart ?? val.length;

    valueRef.current = val;
    rhfOnChange(e);

    const { word, start } = extractCurrentWord(val, cursor);
    wordRef.current = word;
    wordStartRef.current = start;

    if (handleInputChange) {
      handleInputChange(word);
    }

    const matches = filterSuggestions(word);
    setFiltered(matches);
    setActiveIndex(matches.length ? 0 : -1);
    setOpen(matches.length > 0);

    const selectedId = getSelectedId(val);
    if (onSelectedIdChange) {
      onSelectedIdChange(selectedId);
    }
  };

  const applySuggestion = (item) => {
    if (!item) return;

    const insert = getLabel(item).trim();
    const currentText = valueRef.current ?? "";

    // Existing selected labels
    const existing = currentText
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

    const currentWord = wordRef.current.trim();

    // Remove the word currently being typed
    if (currentWord) {
      const lastIndex = existing.findIndex(
        (x) => x.toLowerCase() === currentWord.toLowerCase()
      );

      if (lastIndex !== -1) {
        existing.splice(lastIndex, 1);
      }
    }

    // Prevent duplicate labels
    if (!existing.some((x) => x.toLowerCase() === insert.toLowerCase())) {
      existing.push(insert);
    }

    const newValue = existing.join(" ");

    valueRef.current = newValue;

    onChangeRef.current?.({
      target: {
        name,
        value: newValue,
      },
    });

    // Maintain selected strings
    if (
      !selectedStringsRef.current.some(
        (x) => x.toLowerCase() === insert.toLowerCase()
      )
    ) {
      selectedStringsRef.current.push(insert);
    }

    // Return a single string
    const selectedString = selectedStringsRef.current.join(" ");

    onSelectedIdChange?.(selectedString);

    onSuggestionSelect?.({
      ...item,
      value: newValue,
      selectedString,
    });

    setOpen(false);
    setFiltered([]);
    setActiveIndex(-1);

    wordRef.current = "";
  };

  const handleKeyDown = (e) => {
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (activeIndex >= 0) {
        e.preventDefault();
        applySuggestion(filtered[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef?.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document?.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!wordRef.current) {
      setFiltered([]);
      setOpen(false);
      return;
    }

    const matches = filterSuggestions(wordRef.current);
    setFiltered(matches);
    setActiveIndex(matches.length ? 0 : -1);
    setOpen(matches.length > 0);
  }, [suggestions, filterSuggestions]);


  return (
    <Box position="relative" ref={wrapperRef}>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => {
          onChangeRef.current = field.onChange;
          valueRef.current = field.value ?? "";

          return (
            <>
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={minRows}
                maxRows={maxRows}
                disabled={isDisabled}
                label={isMandatory ? `${label} *` : label}
                error={!!error}
                onChange={(e) => handleChange(e, field.onChange)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
              />

              {open && (
                <Paper
                  elevation={4}
                  sx={{
                    position: "absolute",
                    width: "100%",
                    mt: 0.5,
                    maxHeight: 220,
                    overflowY: "auto",
                    zIndex: 20,
                  }}
                >
                  {filtered?.map((item, i) => (
                    <MenuItem
                      key={`suggestion-${i}-${getLabel(item)}`}
                      selected={i === activeIndex}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        applySuggestion(item);
                      }}
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        lineHeight: 1.4,
                        alignItems: "flex-start",
                        backgroundColor: "transparent",
                        "&.Mui-selected": {
                          backgroundColor: "#c8defa !important",
                          color: "black",
                        },
                      }}
                    >
                      {getLabel(item)}
                    </MenuItem>
                  ))}
                </Paper>
              )}
            </>
          );
        }}
      />
    </Box>
  );
}